import { useState } from "react";
import { T, REL } from "../../theme.js";
import { usePeople } from "../../context/PeopleContext.jsx";
import { Tag } from "../ui/index.jsx";
import { Av } from "../ui/index.jsx";
import { PersonDrawer } from "../people/PersonDrawer.jsx";
import { AddPersonSheet } from "../people/AddPersonSheet.jsx";
import { getDisplayName } from "../../utils/displayName.js";

import {
  HOME_WEEK_DAYS, HOME_WEEK_DATES, HOME_TODAY,
  WEEK_DATES, WEEK_RANGE_STR,
  _CAL_MONTHS, _DAY_NAMES_FULL, _nowDate,
  toLocalYMD, CAL_TYPE_COLORS, crossProviderDedupe,
} from "../../utils/calendarUtils.js";

// ── HAvatar ───────────────────────────────────────────────────────────────────
export function HAvatar({p,size:_size=40,selected}){
  const size=Math.max(_size,24);
  const c=REL[p.type]?.color||T.partner;
  const rings={partner:`2px solid ${c}88`,family:`2px dashed ${c}88`,friend:`2px dotted ${c}88`,coparent:`3px double ${c}`};
  return(<div style={{width:size,height:size,borderRadius:"50%",background:c+"20",border:rings[p.type]||rings.partner,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*.28,fontWeight:700,color:c,fontFamily:"'Crimson Pro',serif",boxShadow:selected?`0 0 0 3px ${c}30`:"none",transition:"all .18s",flexShrink:0,position:"relative",overflow:"hidden"}}>
    {p.photo?<img src={p.photo} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} alt=""/>:p.ini}
  </div>);
}

// ── HFairnessBar ──────────────────────────────────────────────────────────────
function monthlyTgt(p){ return p.tgtMode==="week"?Math.round(p.tgt*4.33):p.tgt; }
function HFairnessBar({p}){
  const {prefs}=usePeople();
  if(!p.tgt||!prefs?.fairnessTracking)return null;
  const pct=Math.round((p.act/monthlyTgt(p))*100);
  const color=pct>=90?T.sage:pct>=60?T.amber:T.red;
  return(<div style={{marginTop:6}}>
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
      <span style={{fontSize:11,color:T.muted}}>fairness</span>
      <span style={{fontSize:11,fontWeight:700,color}}>{pct}%</span>
    </div>
    <div style={{height:4,borderRadius:2,background:T.edge}}>
      <div style={{height:"100%",borderRadius:2,width:`${Math.min(pct,100)}%`,background:`linear-gradient(90deg,${color}AA,${color})`,transition:"width .6s ease"}}/>
    </div>
  </div>);
}

// ── HPersonCard ───────────────────────────────────────────────────────────────
function HPersonCard({p,isSelected,onClick,onOpen}){
  const c=REL[p.type]?.color||T.partner;
  const cfg=REL[p.type];
  const {pendingOutgoingIds,pendingSchedulingIds}=usePeople();
  const isPending=p.linkedUserId&&(pendingOutgoingIds?.has(p.linkedUserId)||pendingSchedulingIds?.has(p.linkedUserId));
  return(<div onClick={onOpen||onClick} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",borderRadius:12,background:isSelected?c+"0E":T.panel,border:`1.5px solid ${isSelected?c+"55":T.edge}`,cursor:"pointer",transition:"all .16s",boxShadow:isSelected?`0 3px 16px ${c}14`:"0 1px 4px rgba(0,0,0,.03)"}}>
    <HAvatar p={p} size={42} selected={isSelected}/>
    <div style={{flex:1,minWidth:0}}>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
        <span style={{fontSize:14,fontWeight:600,color:T.ink}}>{p.name}</span>
        {p.type!=="partner"&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:c+"18",border:`1px solid ${c}30`,color:c,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em"}}>{cfg.label}</span>}
        {isPending&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:T.clay+"18",border:`1px solid ${T.clay}35`,color:T.clay,fontWeight:600}}>Pending</span>}
      </div>
      <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>{p.tags.slice(0,2).map(t=><Tag key={t} label={t} color={c}/>)}</div>
      <HFairnessBar p={p}/>
    </div>
    <div style={{textAlign:"right",flexShrink:0}}>
      <div style={{fontSize:11,fontWeight:600,color:c}}>{p.next||"TBD"}</div>
      <div style={{fontSize:11,color:T.muted,marginTop:1}}>{p.seen ?? 0}d ago</div>
    </div>
  </div>);
}

// ── HRelSection ───────────────────────────────────────────────────────────────
function HRelSection({type,selectedId,onSelect,onOpen}){
  const {people:PEOPLE}=usePeople();
  const cfg=REL[type];
  const group=PEOPLE.filter(p=>p.type===type);
  if(!group.length)return null;
  return(<div style={{marginBottom:24}}>
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
      <span style={{fontSize:14,color:cfg.color}}>{cfg.icon}</span>
      <span style={{fontSize:13,fontWeight:700,color:T.bark}}>{cfg.plural}</span>
      <span style={{fontSize:11,fontWeight:700,color:cfg.color,background:cfg.color+"18",border:`1px solid ${cfg.color}35`,borderRadius:10,padding:"1px 7px"}}>{group.length}</span>
      <div style={{flex:1,height:1,background:cfg.color+"25",marginLeft:4}}/>
      <button style={{fontSize:11,color:cfg.color,background:cfg.color+"12",border:`1px solid ${cfg.color}30`,borderRadius:12,padding:"3px 10px",cursor:"pointer"}}>
        {type==="partner"?"Fairness →":type==="coparent"?"Co-calendar →":"Schedule →"}
      </button>
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {group.map(p=><HPersonCard key={p.id} p={p} isSelected={selectedId===p.id} onClick={()=>onSelect(p.id===selectedId?null:p.id)} onOpen={onOpen?()=>onOpen(p.id):null}/>)}
    </div>
  </div>);
}

// ── HWeekCal ──────────────────────────────────────────────────────────────────
function HWeekCal({onDayClick}){
  const {people:PEOPLE,calEvents}=usePeople();
  const _orbitSlots=calEvents.filter(e=>e.source==="orbit"&&e.id?.startsWith("orbit-sched-")&&!e.allDay);
  const _forDedup=calEvents.filter(e=>{
    if(e.source==="orbit"||e.allDay) return true;
    return !_orbitSlots.some(o=>o.date===e.date&&Math.abs(o.startH-e.startH)<0.1&&Math.abs(o.endH-e.endH)<0.1);
  });
  const deduped=crossProviderDedupe(_forDedup);
  const pmap=Object.fromEntries(PEOPLE.map(p=>[p.id,p]));
  const typesPresent=new Set(deduped.filter(e=>e.person&&pmap[e.person]).map(e=>pmap[e.person].type).filter(Boolean));
  return(<div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:5}}>
      {HOME_WEEK_DAYS.map((d,i)=><div key={d} style={{textAlign:"center",fontSize:11,fontWeight:600,color:i===HOME_TODAY?T.partner:T.muted,textTransform:"uppercase",letterSpacing:"0.07em"}}>{d}</div>)}
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
      {HOME_WEEK_DATES.map((date,i)=>{
        const _wds=toLocalYMD(WEEK_DATES[i]);
        const evts=deduped.filter(e=>typeof e.day==="number"&&e.day===i&&(!e.date||e.date===_wds));
        const isToday=i===HOME_TODAY;
        return(<div key={date} onClick={()=>onDayClick&&onDayClick(i)} style={{minHeight:72,borderRadius:10,background:isToday?"#FDF0E6":T.panel,border:`1.5px solid ${isToday?T.partner+"50":T.edge}`,padding:"5px 5px 4px",cursor:onDayClick?"pointer":"default",transition:"box-shadow .12s"}}>
          <div style={{fontSize:12,fontFamily:"'Crimson Pro',serif",color:isToday?T.partner:T.mid,fontWeight:isToday?700:400,marginBottom:3}}>{date}</div>
          {evts.map((ev,ei)=>{
            const p=ev.person?pmap[ev.person]:null;
            const c=p?REL[p.type]?.color||T.partner:CAL_TYPE_COLORS[ev.type]||T.muted;
            return(<div key={ei} style={{background:c+"22",borderLeft:`2.5px solid ${c}`,borderRadius:"0 4px 4px 0",padding:"2px 4px",marginBottom:2,fontSize:11,color:c,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",display:"flex",alignItems:"center",gap:3}}>
              {p&&<span style={{fontSize:11}}>{REL[p.type]?.icon}</span>}{ev.label.split("–")[0].split(" ")[0]}
            </div>);
          })}
        </div>);
      })}
    </div>
    {typesPresent.size>0&&<div style={{display:"flex",gap:12,marginTop:8,flexWrap:"wrap"}}>
      {Object.entries(REL).map(([key,cfg])=>{
        if(!typesPresent.has(key))return null;
        return(<div key={key} style={{display:"flex",alignItems:"center",gap:4}}><span style={{fontSize:11,color:cfg.color}}>{cfg.icon}</span><span style={{fontSize:11,color:T.muted}}>{cfg.label}</span></div>);
      })}
    </div>}
  </div>);
}

// ── Quick Actions — shared data + grid ────────────────────────────────────────
const QUICK_ACTIONS=[
  {iconType:"cal",label:"1:1 Date",       sub:"Partner",          color:T.partner,nav:"schedule",type:"partner"},
  {iconType:"cal",label:"Group hang",     sub:"Multi-person",     color:T.friend,nav:"schedule",mode:"group"},
  {iconType:"scale",label:"Rotation pick",sub:"Who's next?",      color:"#C4A050",nav:"people",pplType:"fairness"},
  {iconType:"cal",label:"Family time",    sub:"Family / coparent",color:T.family,nav:"schedule",type:"family"},
  {iconType:"emoji",icon:"☕",label:"Friend catch-up",sub:"Low-key scheduling",color:T.friend,nav:"schedule",type:"friend"},
];
function QAIcon({type,icon,color,size}){
  if(type==="cal")return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="8" y2="18"/><line x1="12" y1="18" x2="12" y2="18"/></svg>;
  if(type==="scale")return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="3" x2="12" y2="21"/><path d="M5 21h14"/><path d="M5 8l-3 6h6l-3-6z"/><path d="M19 8l-3 6h6l-3-6z"/><line x1="5" y1="8" x2="19" y2="8"/></svg>;
  return <span style={{fontSize:size,lineHeight:1}}>{icon}</span>;
}
function QuickActionsGrid({onNav,compact}){
  const sz=compact?30:34;
  const iconSz=compact?14:17;
  const pad=compact?10:13;
  return(
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:compact?8:10}}>
      {QUICK_ACTIONS.map((a,i)=>{
        const isWide=i===QUICK_ACTIONS.length-1&&QUICK_ACTIONS.length%2===1;
        return(
          <div key={i} onClick={()=>onNav(a.nav,null,null,null,a.type||null,a.mode||null,a.pplType||null)}
            style={{padding:pad,borderRadius:compact?12:14,background:T.surface,border:`1px solid ${T.edge}`,cursor:"pointer",gridColumn:isWide?"1 / -1":undefined,display:isWide?"flex":undefined,alignItems:isWide?"center":undefined,gap:isWide?10:undefined,transition:"border-color .15s"}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=a.color+"60";}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=T.edge;}}>
            <div style={{width:sz,height:sz,borderRadius:compact?7:9,background:a.color+"18",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:isWide?0:6,flexShrink:0}}>
              <QAIcon type={a.iconType} icon={a.icon} color={a.color} size={iconSz}/>
            </div>
            <div>
              <p style={{fontSize:compact?11:13,fontWeight:600,color:T.ink,marginBottom:1}}>{a.label}</p>
              <p style={{fontSize:compact?10:11,color:T.muted}}>{a.sub}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── HNotifRow ─────────────────────────────────────────────────────────────────
function HNotifRow({notif,onNav}){
  const {people}=usePeople();
  const cfg=REL[notif.type]||REL.friend;
  const c=cfg.color;
  const matchedPerson=people.find(p=>p.ini===notif.ini&&p.type===notif.type);
  const photo=notif.photo||matchedPerson?.photo||null;
  const statusColor=notif.status==="confirmed"?T.sage:notif.status==="declined"?T.red:notif.status==="pending"?T.amber:T.muted;
  const statusLabel=notif.status==="confirmed"?"Confirmed ✓":notif.status==="declined"?"Declined":notif.kind==="reschedule"?"Reschedule req":notif.kind==="confirmed"?"Confirmed ✓":notif.kind==="info"?"Info":"Proposal";
  return(
    <div onClick={()=>onNav&&onNav("inbox",null,null,notif.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 10px",borderRadius:10,marginBottom:5,background:notif.read?T.panel:c+"08",border:`1px solid ${notif.read?T.edge:c+"30"}`,cursor:"pointer",transition:"all .15s"}}>
      <div style={{width:32,height:32,borderRadius:"50%",background:c+"20",border:`1.5px solid ${c}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:c,fontFamily:"'Crimson Pro',serif",flexShrink:0,position:"relative",overflow:"hidden"}}>
        {photo?<img src={photo} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} alt=""/>:notif.ini}
        {!notif.read&&<div style={{position:"absolute",top:-2,right:-2,width:8,height:8,borderRadius:"50%",background:T.red,border:"1.5px solid white"}}/>}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <p style={{fontSize:12.5,color:T.ink,lineHeight:1.3,fontWeight:notif.read?400:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{notif.subject}</p>
        <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
          <span style={{fontSize:11,color:c}}>{cfg.icon} {cfg.label}</span>
          {notif.time&&<span style={{fontSize:11,color:T.muted}}>· {notif.time}</span>}
          <span style={{fontSize:11,color:statusColor,background:statusColor+"18",padding:"1px 5px",borderRadius:6,fontWeight:600}}>{statusLabel}</span>
        </div>
      </div>
    </div>
  );
}

// ── HPeopleSummary — all relationship groups with person cards ────────────────
function HPeopleSummary({onOpen,onNav}){
  const {people:PEOPLE,prefs}=usePeople();
  const groups=[
    {type:"partner",list:PEOPLE.filter(p=>p.type==="partner")},
    {type:"family",list:PEOPLE.filter(p=>p.type==="family")},
    {type:"friend",list:PEOPLE.filter(p=>p.type==="friend")},
    {type:"coparent",list:PEOPLE.filter(p=>p.type==="coparent")},
  ].filter(g=>g.list.length>0);
  if(!groups.length) return null;
  return(<div style={{background:T.surface,borderRadius:16,padding:14,border:`1px solid ${T.edge}`,boxShadow:"0 2px 12px rgba(60,40,20,.05)"}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
      <p style={{fontSize:12,fontWeight:600,color:T.bark}}>Your people</p>
      <button onClick={()=>onNav("people")} style={{fontSize:11,color:T.clay,background:"none",border:"none",cursor:"pointer",fontWeight:600,padding:0}}>View all →</button>
    </div>
    {groups.map(({type,list},gi)=>{
      const cfg=REL[type];
      return(<div key={type} style={{marginBottom:gi<groups.length-1?10:0}}>
        <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:6}}>
          <span style={{fontSize:11,color:cfg.color}}>{cfg.icon}</span>
          <span style={{fontSize:11,fontWeight:700,color:T.bark,textTransform:"uppercase",letterSpacing:"0.05em"}}>{cfg.plural}</span>
          <span style={{fontSize:11,fontWeight:700,color:cfg.color,background:cfg.color+"18",borderRadius:7,padding:"0 5px"}}>{list.length}</span>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {list.slice(0,3).map(p=>{
            const showFairness=type==="partner"&&p.tgt&&prefs?.fairnessTracking;
            const pct=showFairness?Math.round(((p.act||0)/monthlyTgt(p))*100):0;
            const fColor=pct>=90?T.sage:pct>=60?T.amber:T.red;
            return(<div key={p.id} onClick={()=>onOpen&&onOpen(p.id)} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 8px",borderRadius:10,background:T.panel,border:`1px solid ${T.edge}`,cursor:"pointer",transition:"background .12s"}}
              onMouseEnter={e=>e.currentTarget.style.background=cfg.color+"08"}
              onMouseLeave={e=>e.currentTarget.style.background=T.panel}>
              <HAvatar p={p} size={26}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{fontSize:11,fontWeight:600,color:T.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</span>
                  {showFairness&&<span style={{fontSize:11,fontWeight:700,color:fColor}}>{pct}%</span>}
                </div>
                {showFairness&&<div style={{height:2,borderRadius:1,background:T.edge,marginTop:2}}><div style={{height:"100%",borderRadius:1,width:`${Math.min(pct,100)}%`,background:fColor,transition:"width .4s ease"}}/></div>}
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:11,fontWeight:600,color:cfg.color}}>{p.next||"TBD"}</div>
                <div style={{fontSize:11,color:T.muted}}>{p.seen ?? 0}d ago</div>
              </div>
            </div>);
          })}
          {list.length>3&&<button onClick={()=>onNav("people",null,null,null,null,null,type)} style={{fontSize:11,color:T.muted,background:T.mist,border:`1px solid ${T.edge}`,borderRadius:8,padding:"4px",cursor:"pointer",textAlign:"center"}}>+{list.length-3} more</button>}
        </div>
        {gi<groups.length-1&&<div style={{height:1,background:T.edge,marginTop:10}}/>}
      </div>);
    })}
  </div>);
}

// ── Greeting helpers ──────────────────────────────────────────────────────────
function _timeGreeting(){
  const h=new Date().getHours();
  if(h<12) return "Good morning";
  if(h<17) return "Good afternoon";
  return "Good evening";
}
function buildGreeting(){
  const {people:PEOPLE,user,calEvents}=usePeople();
  const greetingName = getDisplayName(user,"there");
  const salutation=_timeGreeting();
  const todayStr=toLocalYMD(_nowDate);
  const tonight=calEvents.filter(e=>e.day===HOME_TODAY&&e.person&&(!e.date||e.date===todayStr));
  const _seenTonightIds=new Set();
  const tp=tonight.map(e=>PEOPLE.find(p=>p.id===e.person)).filter(p=>{
    if(!p||_seenTonightIds.has(p.id)) return false;
    _seenTonightIds.add(p.id); return true;
  });
  if(tp.length>0){
    const names=tp.map(p=>p.name).join(" & ");
    const type=tp[0].type;
    const sub=type==="partner"?`${names} is free tonight. 💜`:type==="family"?`${names} — family time tonight. 👨‍👩‍👧`:`Catching up with ${names} tonight. 👥`;
    return{headline:`${salutation}, ${greetingName}.`,sub,accent:REL[type].color,calDay:HOME_TODAY};
  }
  const av=PEOPLE.filter(p=>p.avail);
  if(av.length>0)return{headline:`${salutation}, ${greetingName}.`,sub:`${av.map(p=>p.name).join(", ")} ${av.length===1?"is":"are"} available now.`,accent:REL[av[0].type].color,calDay:null};
  const todayName=_DAY_NAMES_FULL[new Date().getDay()];
  return{headline:`${salutation}, ${greetingName}.`,sub:`A quiet ${todayName} — space for yourself.`,accent:T.muted,calDay:null};
}

function _liveDateStr(){
  const d=new Date();
  return`${_DAY_NAMES_FULL[d.getDay()]}, ${_CAL_MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// ── HomeContent ───────────────────────────────────────────────────────────────
export function HomeContent({onNav,isMobile}){
  const {people:PEOPLE,notifs,addPerson,user,calEvents,googleCalStatus,icloudCalStatus,outlookCalStatus,ofwCalStatus,yahooCalStatus,caldavCalStatus}=usePeople();
  const _pmap=Object.fromEntries(PEOPLE.map(p=>[p.id,p]));
  const liveDateStr=_liveDateStr();
  const _homeName=getDisplayName(user,"You");
  const _homeParts=_homeName.split(/\s+/).filter(Boolean);
  const userIni=_homeParts.length>=2?(_homeParts[0][0]+_homeParts[_homeParts.length-1][0]).toUpperCase():(_homeParts[0]?.slice(0,2).toUpperCase()||"?");
  const userPhoto=user?.user_metadata?.photo||null;
  const [selectedId,setSelectedId]=useState(null);
  const [drawerId,setDrawerId]=useState(null);
  const [addPersonOpen,setAddPersonOpen]=useState(false);
  const [connReqSentPopup,setConnReqSentPopup]=useState(false);
  const [updatesUnreadOnly,setUpdatesUnreadOnly]=useState(false);
  const greeting=buildGreeting();
  const calEventsLoading=googleCalStatus==="loading"||icloudCalStatus==="loading"||outlookCalStatus==="loading"||ofwCalStatus==="loading"||yahooCalStatus==="loading"||caldavCalStatus==="loading";

  // ── Mobile ──
  if(isMobile) return(<>
    <div style={{flex:1,overflowY:"auto"}}>
    <div style={{padding:"8px 18px 0"}}>
      <p style={{fontSize:11,color:T.muted,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:4}}>{liveDateStr}</p>
      <h1 style={{fontSize:24,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,marginBottom:4,letterSpacing:"-0.02em",lineHeight:1.25}}>
        {greeting.headline}<br/>
                {greeting.calDay!=null
                  ?<span onClick={()=>onNav("calendars",null,greeting.calDay)} style={{color:greeting.accent,fontStyle:"italic",cursor:"pointer",borderBottom:`1px dashed ${greeting.accent}66`,paddingBottom:1,transition:"opacity .15s",fontSize:greeting.sub.length>50?17:greeting.sub.length>35?20:undefined}}>{greeting.sub} ↗</span>
                  :<span style={{color:greeting.accent,fontStyle:"italic",fontSize:greeting.sub.length>50?17:greeting.sub.length>35?20:undefined}}>{greeting.sub}</span>
                }
      </h1>
      {(()=>{
        const avail=PEOPLE.filter(p=>p.avail);
        return avail.length>0?(
          <div style={{display:"flex",gap:14,overflowX:"auto",paddingBottom:4,marginBottom:8}}>
            {avail.map(p=>(
              <div key={p.id} onClick={()=>setDrawerId(p.id)} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,flexShrink:0,cursor:"pointer"}}>
                <HAvatar p={p} size={48}/><span style={{fontSize:11,color:T.bark,fontWeight:500}}>{p.name}</span>
              </div>
            ))}
          </div>
        ):null;
      })()}
      <button onClick={()=>onNav("schedule")} style={{display:"flex",alignItems:"center",gap:9,width:"100%",padding:"13px 18px",borderRadius:14,background:T.bark,color:T.parchment,border:"none",cursor:"pointer",fontSize:15,fontWeight:600,letterSpacing:"-0.01em",boxShadow:"0 4px 16px rgba(60,40,20,.20)",marginBottom:14,marginTop:8}}>
        Schedule something
      </button>
      {/* ── 7-day dot strip ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:16}}>
        {HOME_WEEK_DAYS.map((d,i)=>{
          const _wds=toLocalYMD(WEEK_DATES[i]);
          const _dayOrbit=calEvents.filter(e=>e.source==="orbit"&&e.id?.startsWith("orbit-sched-")&&!e.allDay&&e.day===i&&e.date===_wds);
          const dayEvts=calEvents.filter(e=>typeof e.day==="number"&&e.day===i&&(!e.date||e.date===_wds)&&(e.source==="orbit"||e.allDay||!_dayOrbit.some(o=>Math.abs(o.startH-e.startH)<0.1&&Math.abs(o.endH-e.endH)<0.1)));
          const count=dayEvts.length;
          const isToday=i===HOME_TODAY;
          const dotEvts=dayEvts.filter(e=>e.person).slice(0,3);
          return(
            <div key={d} onClick={()=>onNav("calendars",null,i)} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"6px 2px",borderRadius:10,background:isToday?"#FEF3E8":T.surface,border:`1px solid ${isToday?T.partner+"50":T.edge}`,cursor:"pointer"}}>
              <span style={{fontSize:11,fontWeight:600,color:isToday?T.partner:T.muted,textTransform:"uppercase",letterSpacing:"0.06em"}}>{d}</span>
              <span style={{fontSize:11,fontFamily:"'Crimson Pro',serif",color:isToday?T.partner:T.mid,fontWeight:isToday?700:400}}>{HOME_WEEK_DATES[i]}</span>
              {calEventsLoading?(
                <div style={{display:"flex",gap:2,justifyContent:"center"}}>
                  {[0,1,2].map(j=><div key={j} style={{width:5,height:5,borderRadius:"50%",background:T.edge,opacity:0.4}}/>)}
                </div>
              ):count>0?(
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  <div style={{display:"flex",gap:2,flexWrap:"wrap",justifyContent:"center"}}>
                    {dotEvts.map((ev,ei)=>{
                      const p=ev.person?_pmap[ev.person]:null;
                      const c=p?REL[p.type]?.color||T.partner:CAL_TYPE_COLORS[ev.type]||T.muted;
                      return <div key={ei} style={{width:5,height:5,borderRadius:"50%",background:c}}/>;
                    })}
                    {count>0&&dotEvts.length===0&&<div style={{width:5,height:5,borderRadius:"50%",background:T.muted+"60"}}/>}
                  </div>
                  {count>3&&<span style={{fontSize:11,color:T.muted}}>+{count-3}</span>}
                </div>
              ):(
                <span style={{fontSize:11,color:T.edge,lineHeight:1}}>–</span>
              )}
            </div>
          );
        })}
      </div>
      <div style={{marginBottom:20}}>
        <QuickActionsGrid onNav={onNav}/>
      </div>
    </div>
  </div>
  {drawerId&&<PersonDrawer isMobile personId={drawerId} onClose={()=>setDrawerId(null)} onSchedule={()=>{setDrawerId(null);onNav("schedule",drawerId);}}/>}
  </>);

  // ── Desktop layout ──
  return(<>
  <div style={{flex:1,overflowY:"auto",padding:"28px 24px"}}>
    <div style={{maxWidth:1200,margin:"0 auto"}}>
      <div style={{animation:"fadeUp .3s ease"}}>

          {/* ── Greeting row (full-width) ── */}
          <div style={{marginBottom:16}}>
            <p style={{fontSize:11,color:T.muted,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:4}}>{liveDateStr}</p>
            <h1 style={{fontSize:32,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,letterSpacing:"-0.02em",lineHeight:1.2,marginBottom:14}}>
              {greeting.headline}<br/>
              {greeting.calDay!=null
                ?<span onClick={()=>onNav("calendars",null,greeting.calDay)} style={{color:greeting.accent,fontStyle:"italic",cursor:"pointer",borderBottom:`1px dashed ${greeting.accent}66`,paddingBottom:1,transition:"opacity .15s",fontSize:greeting.sub.length>50?22:greeting.sub.length>35?26:undefined}}>{greeting.sub} ↗</span>
                :<span style={{color:greeting.accent,fontStyle:"italic",fontSize:greeting.sub.length>50?22:greeting.sub.length>35?26:undefined}}>{greeting.sub}</span>
              }
            </h1>
            <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <button onClick={()=>onNav("schedule")} style={{display:"inline-flex",alignItems:"center",gap:9,padding:"11px 22px",borderRadius:12,background:T.bark,color:T.parchment,border:"none",cursor:"pointer",fontSize:14,fontWeight:600,letterSpacing:"-0.01em",boxShadow:"0 4px 16px rgba(60,40,20,.22)",transition:"opacity .15s",flexShrink:0}}>
                Schedule something
              </button>
              {/* Available now — capped at 4 badges + overflow count */}
              {(()=>{
                const avail=PEOPLE.filter(p=>p.avail);
                const shown=avail.slice(0,4);
                const overflow=avail.length-shown.length;
                const nextUp=PEOPLE.filter(p=>!p.avail&&p.next)[0]||null;
                if(avail.length===0) return(
                  <span style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>
                    No one available{nextUp?` — next: ${nextUp.name} at ${nextUp.next}`:""}
                  </span>
                );
                return(<div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                  {shown.map(p=>{
                    const c=REL[p.type].color;
                    return(<div key={p.id} onClick={()=>onNav("schedule",p.id)} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px 4px 5px",borderRadius:20,background:c+"12",border:`1px solid ${c}30`,cursor:"pointer",flexShrink:0}}>
                      <HAvatar p={p} size={22}/>
                      <span style={{fontSize:12,fontWeight:600,color:T.ink}}>{p.name}</span>
                      <span style={{fontSize:11,color:c,fontWeight:600}}>{p.next}</span>
                    </div>);
                  })}
                  {overflow>0&&<span style={{fontSize:11,color:T.muted,padding:"4px 8px",borderRadius:20,background:T.mist,border:`1px solid ${T.edge}`,flexShrink:0}}>+{overflow} more</span>}
                </div>);
              })()}
            </div>
          </div>

          {/* ── Main 3-column grid ── */}
          {/* Col 3 uses minmax so it never collapses below 220px on mid-range viewports */}
          <div style={{display:"grid",gridTemplateColumns:"minmax(280px,1.6fr) minmax(220px,1fr) minmax(220px,1fr)",gap:16,alignItems:"start"}}>

            {/* Col 1 — Week calendar */}
            <div style={{borderRadius:16,overflow:"hidden",border:`1.5px solid ${T.partner}30`,boxShadow:"0 3px 18px rgba(60,40,20,.08)"}}>
              <div style={{background:"#FEF3E8",padding:"13px 20px 11px",borderBottom:`1px solid ${T.edge}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <h2 style={{fontSize:14,fontWeight:600,color:T.ink}}>This week</h2>
                  <span style={{fontSize:11,color:T.muted}}>{WEEK_RANGE_STR}</span>
                </div>
              </div>
              <div style={{background:T.surface,padding:20}}>
                <HWeekCal onDayClick={i=>{ onNav("calendars",null,i); }}/>
              </div>
            </div>

            {/* Col 2 — Updates */}
            {(()=>{const _unreadCount=notifs.filter(n=>!n.read).length;const _hasUnread=_unreadCount>0;return(
            <div style={{background:T.surface,borderRadius:16,padding:18,border:`1.5px solid ${_hasUnread?T.partner+"50":T.edge}`,boxShadow:_hasUnread?`0 2px 16px ${T.partner}14`:"0 2px 12px rgba(60,40,20,.05)",transition:"border-color .2s, box-shadow .2s"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <h3 style={{fontSize:14,fontWeight:600,color:T.ink,display:"flex",alignItems:"center",gap:6}}>Updates{_hasUnread&&<span style={{fontSize:11,background:T.red,color:"white",borderRadius:8,padding:"1px 6px",fontWeight:700,lineHeight:1.4}}>{_unreadCount}</span>}</h3>
                {_hasUnread&&(
                  <span onClick={()=>setUpdatesUnreadOnly(v=>!v)} style={{fontSize:11,background:T.partner,color:"white",borderRadius:10,padding:"1px 7px",fontWeight:700,cursor:"pointer",outline:updatesUnreadOnly?`2px solid ${T.partner}`:"none",outlineOffset:1,opacity:updatesUnreadOnly?1:0.85,transition:"all .15s"}}>{_unreadCount} new {updatesUnreadOnly?"✕":""}</span>
                )}
              </div>
              {[...(updatesUnreadOnly?notifs.filter(n=>!n.read):notifs)].sort((a,b)=>(b._ts||0)-(a._ts||0)).slice(0,5).map(n=><HNotifRow key={n.id} notif={n} onNav={onNav}/>)}
              <button onClick={()=>onNav("inbox")} style={{width:"100%",marginTop:8,padding:"7px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,fontSize:11,color:T.muted,cursor:"pointer",fontWeight:500}}>View all in inbox →</button>
            </div>);})()}

            {/* Col 3 — Quick actions (top) + People (bottom) */}
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <QuickActionsGrid onNav={onNav} compact/>
              <HPeopleSummary onOpen={id=>setDrawerId(id)} onNav={onNav}/>
            </div>

          </div>


      </div>
    </div>
  </div>
  {drawerId&&<PersonDrawer isMobile personId={drawerId} onClose={()=>setDrawerId(null)} onSchedule={()=>{setDrawerId(null);onNav("schedule");}}/>}
  {addPersonOpen&&<AddPersonSheet onClose={()=>setAddPersonOpen(false)} onAdd={(p)=>{addPerson(p);setAddPersonOpen(false);if(p.linkedUserId)setConnReqSentPopup(true);}}/>}
  {connReqSentPopup&&(
    <div onClick={()=>setConnReqSentPopup(false)} style={{position:"fixed",inset:0,background:"rgba(30,16,8,0.45)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",animation:"fadeIn .15s ease"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:T.surface,borderRadius:18,padding:"28px 28px 20px",maxWidth:300,width:"88%",boxShadow:"0 8px 48px rgba(30,16,8,.22)",textAlign:"center",animation:"fadeUp .2s ease"}}>
        <div style={{fontSize:28,marginBottom:10}}>📨</div>
        <p style={{fontSize:16,fontFamily:"'Crimson Pro',serif",fontWeight:600,color:T.ink,marginBottom:6}}>Connection request sent</p>
        <p style={{fontSize:13,color:T.muted,lineHeight:1.45,marginBottom:20}}>They'll see your request and can accept or decline.</p>
        <button onClick={()=>setConnReqSentPopup(false)} style={{width:"100%",padding:"11px",borderRadius:11,background:T.clay,border:"none",color:"white",fontSize:14,fontWeight:600,cursor:"pointer"}}>OK</button>
      </div>
    </div>
  )}
  </>);
}
